"use client";

import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, type Variants } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const ASSET_PREFIX = "/hong-kong-accounting-services";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const cardsContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 48 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] },
  },
};

const businessTypes = [
  {
    title: "E-commerce Sellers",
    description:
      "Trading across marketplaces and storefronts, with payouts and processor fees that need to land correctly in the accounts.",
    image: `${ASSET_PREFIX}/slide-1.svg`,
    imageClassName: "h-[417px] rounded-3xl object-cover",
  },
  {
    title: "International Founders",
    description:
      "Running a Hong Kong company from overseas and wanting accounts prepared by a qualified team locally.",
    image: `${ASSET_PREFIX}/slide-2.svg`,
    imageClassName: "h-[277px]",
  },
  {
    title: "Growing Businesses Preparing for Audit",
    description:
      "Approaching a financial year end and needing accounts that are genuinely ready to hand to an auditor.",
    image: `${ASSET_PREFIX}/slide-3.svg`,
    imageClassName: "h-[417px]",
  },
  {
    title: "Agencies and Service Businesses",
    description:
      " Invoicing clients internationally and needing accounts that hold up when a bank or the Inland Revenue Department wants to see them.",
    image: `${ASSET_PREFIX}/slide-4.svg`,
    imageClassName: "h-[277px] rounded-3xl object-cover",
  },
  {
    title: "Founders Who Have Fallen Behind",
    description:
      "Behind on having proper accounts prepared and wanting the position brought current before audit or filing deadlines catch up.",
    image: `${ASSET_PREFIX}/slide-5.svg`,
    imageClassName: "h-[417px] rounded-3xl object-cover",
  },
  {
    title: "Companies Comparing Bookkeeping to Accounting",
    description:
      "Have someone recording transactions but aren’t sure that’s the same as having accounts prepared. It isn’t, and this is the part that’s often missing.",
    image: `${ASSET_PREFIX}/slide-6.svg`,
    imageClassName: "h-[277px] rounded-3xl object-cover",
  },
];

export const BusinessRegistrationCertificateSection = (): JSX.Element => {
  const trackRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const syncArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 1);
    setAtEnd(track.scrollLeft >= maxScroll - 1);
  }, []);

  useEffect(() => {
    syncArrows();
    window.addEventListener("resize", syncArrows);
    return () => {
      window.removeEventListener("resize", syncArrows);
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [syncArrows]);

  // Slow, eased scroll — `behavior: "smooth"` is too fast and its duration
  // can't be tuned, so animate scrollLeft ourselves.
  const scrollTo = (edge: "start" | "end") => {
    const track = trackRef.current;
    if (!track) return;

    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
    }

    const from = track.scrollLeft;
    const to = edge === "start" ? 0 : track.scrollWidth - track.clientWidth;
    const distance = to - from;
    if (distance === 0) return;

    const duration = 1100;
    const start = performance.now();
    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      track.scrollLeft = from + distance * easeInOutCubic(progress);
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(step);
      } else {
        animationRef.current = null;
        syncArrows();
      }
    };

    animationRef.current = requestAnimationFrame(step);
  };

  return (
    <section
      aria-labelledby="business-registration-heading"
      className="relative flex w-full flex-col items-center gap-16 bg-black px-4 pt-8 sm:pt-20 sm:px-6"
    >
      <header className="flex w-full max-w-[937px] flex-col items-center">
        <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] tracking-[0] text-white backdrop-blur-[3.5px]">
            Who it's for
          </Badge>
        <div className="flex w-full flex-col items-center gap-4 pt-6">
          <motion.h2
            id="business-registration-heading"
            className="w-full [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-[34px] font-normal leading-tight tracking-[-1.92px] text-white [text-shadow:0px_4px_24px_#0000000d] sm:text-5xl sm:leading-[64px]"
            variants={headingVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Running a Company That Needs More
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            Than a Year End Spreadsheet?
          </motion.h2>
          <motion.p
            className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-center text-lg font-normal leading-6 text-[#fefefe] sm:text-xl"
            variants={headingVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            MuteTaxes Works With Founders Building International Businesses
          </motion.p>
        </div>
      </header>
      <div className="flex w-full max-w-[1224px] flex-col gap-6">
        <div className="hidden justify-end gap-3 lg:flex">
          <button
            type="button"
            aria-label="Show previous cards"
            onClick={() => scrollTo("start")}
            disabled={atStart}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[#6a6a6a80] text-white backdrop-blur-[3.5px] transition-opacity hover:bg-[#6a6a6ab3] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Show next cards"
            onClick={() => scrollTo("end")}
            disabled={atEnd}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[#6a6a6a80] text-white backdrop-blur-[3.5px] transition-opacity hover:bg-[#6a6a6ab3] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <motion.div
          ref={trackRef}
          onScroll={syncArrows}
          className="grid w-full grid-cols-2 items-start gap-x-3 gap-y-8 sm:grid-cols-3 sm:gap-6 lg:flex lg:overflow-x-auto lg:pb-2 lg:[scrollbar-width:none] lg:[&::-webkit-scrollbar]:hidden"
          variants={cardsContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {businessTypes.map((businessType) => (
            <motion.div key={businessType.title} variants={cardVariants} className="min-w-0 lg:w-[387px] lg:min-w-[387px]">
              <Card className="flex h-full min-w-0 flex-col border-0 bg-transparent text-white shadow-none">
                <CardContent className="flex flex-col gap-3 p-0 sm:gap-6">
                  <img
                    className={`w-full object-cover max-sm:h-28 max-sm:rounded-xl ${businessType.imageClassName}`}
                    alt="Rectangle"
                    src={businessType.image}
                  />
                  <div className="flex flex-col items-start gap-2 sm:gap-3">
                    <h3 className="[font-family:'Suisse_Intl_Trial-Bold',Helvetica] text-base font-bold leading-4 text-white sm:text-2xl sm:leading-7">
                      {businessType.title}
                    </h3>
                    <p className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-xs font-normal leading-4 text-[#858892] sm:text-base sm:leading-6">
                      {businessType.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
